import {Icon} from "@blueprintjs/core";
import styled from "@emotion/styled";
import {LinearProgress} from "@mui/material";
import {COLORS} from "../../constants/constants";
import {OptimizationObjective} from "../settings/enums/RoutePlanning";

interface PlanningInProgressProps {
  optimizationObjective: OptimizationObjective;
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  width: 100%;
  max-width: 640px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const IconTile = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${COLORS.BLUE_VERY_LIGHT};
`;

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: ${COLORS.GRAY_800};
`;

const Blurb = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const TimingLine = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 14px;
  line-height: 1.5;
  color: ${COLORS.TEXT_GRAY_MEDIUM};
  text-wrap: pretty;

  .bp5-icon {
    flex-shrink: 0;
    margin-top: 3px;
  }
`;

const Steps = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
`;

// The connector runs from under each step's icon down to the next icon; the last step has none.
const Step = styled.li`
  position: relative;
  display: flex;
  gap: 16px;
  padding-bottom: 24px;

  &:last-of-type {
    padding-bottom: 0;
  }

  &:not(:last-of-type)::before {
    content: "";
    position: absolute;
    top: 40px;
    bottom: 8px;
    left: 15px;
    width: 2px;
    background: ${COLORS.COLOR_BORDER_LIGHT};
  }
`;

const StepIcon = styled.div<{tone: "success" | "info"}>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({tone}) => (tone === "success" ? COLORS.GREEN_LIGHT : COLORS.BLUE_VERY_LIGHT)};
`;

const StepText = styled.p`
  margin: 0;
  padding-top: 4px;
  font-size: 16px;
  line-height: 1.5;
  color: ${COLORS.TEXT_GRAY_MEDIUM};
  /* Avoid a single word wrapping alone on the last line (e.g. "page.") for the longer objective phrase. */
  text-wrap: pretty;

  strong {
    font-weight: 600;
    color: ${COLORS.TEXT_BLACK};
  }
`;

/**
 * DP-3172: the stage-3 screen shown while the itinerary is being calculated (replaces the DP-3112 blurb
 * layout). The ticks are static decoration: the backend reports no per-phase progress. The bold phrase
 * reflects the selected optimization objective, which is always concrete here (Both is blocked at stage 1).
 */
const PlanningInProgress = ({optimizationObjective}: PlanningInProgressProps) => (
  <Root>
    <Header>
      <IconTile>
        <Icon icon="route" size={24} color={COLORS.BLUE_500} />
      </IconTile>
      <Title>Itinerary Planning in Progress...</Title>
    </Header>
    <LinearProgress
      data-testid="route-planning-progress-bar"
      aria-label="Itinerary planning in progress"
      sx={{
        // Spans the text column below it and shrinks with it.
        width: "100%",
        height: 6,
        // A string: a bare number here is multiplied by the MUI theme's borderRadius (4px).
        borderRadius: "3px",
        bgcolor: COLORS.COLOR_BORDER_LIGHT,
        "& .MuiLinearProgress-bar": {borderRadius: "3px", bgcolor: COLORS.BLUE_500}
      }}
    />
    <Blurb data-testid="route-planning-calculating-blurb">
      <TimingLine data-testid="route-planning-calculating-eta">
        <Icon icon="time" size={14} color={COLORS.TEXT_GRAY_MEDIUM} />
        <span>
          Depending on your inputs, particularly how many stops &amp; drivers, this may take anywhere from 30 seconds to
          30 minutes.
        </span>
      </TimingLine>
      <Steps>
        <Step data-testid="route-planning-calculating-step">
          <StepIcon tone="success">
            <Icon icon="tick" color={COLORS.GREEN_SUCCESS} />
          </StepIcon>
          <StepText>
            During this process we are analyzing each job and every stop on it for the location, the time window
            requirements, and more.
          </StepText>
        </Step>
        <Step data-testid="route-planning-calculating-step">
          <StepIcon tone="success">
            <Icon icon="tick" color={COLORS.GREEN_SUCCESS} />
          </StepIcon>
          <StepText>
            We are also analyzing the drivers start and end locations, start time, dwell time, a maximum 8 hour shift
            for each driver, and driver skills requirements if relevant.
          </StepText>
        </Step>
        <Step data-testid="route-planning-calculating-step">
          <StepIcon tone="info">
            <Icon icon="map" color={COLORS.BLUE_500} />
          </StepIcon>
          <StepText>
            With these factors in mind an output will be given that{" "}
            <strong>
              {optimizationObjective === OptimizationObjective.BalanceWorks
                ? "divides work evenly as efficiently as possible"
                : "minimizes time in transit"}
            </strong>{" "}
            that you can review, adjust and confirm with the aid of a map on the next page.
          </StepText>
        </Step>
      </Steps>
    </Blurb>
  </Root>
);

export default PlanningInProgress;
